import { notesInterface } from 'Interfaces/FDPInterfaces/tmsInterfaces';
import {
  bulkEditDataInterface,
  filterInterface,
} from 'Interfaces/FDPInterfaces/unconstrainedForecastBulkEdit';
import { useStore } from 'hooks/useStore';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { unconstrainedForecastService } from 'services/fdp/unconstrainedForecastService';
import { convertIsoToDateString } from 'utils/dateUtilities';
import { deepCopy } from 'utils/deepCopy';

// The backend already fills months 13-15 forward from month 12, but the exact
// ForecastPeriod strings are computed server-side while the column dates are
// computed independently on the client (see useGridSection.logic.ts). If those
// two ever drift, an exact string match can miss the extended months entirely.
// Falling back to positional matching (Nth chronological entry === Nth column)
// keeps the extended months populated even when the date strings don't line up.
export const findForecastEntryByPeriodOrIndex = <T extends { ForecastPeriod: string }>(
  forecastData: T[],
  forecastPeriod: string,
  monthIndex?: number
): T | undefined => {
  const exact = forecastData.find(fd => fd.ForecastPeriod === forecastPeriod);
  if (exact) return exact;
  if (monthIndex === undefined || forecastData.length === 0) return undefined;

  const sorted = [...forecastData].sort((a, b) =>
    a.ForecastPeriod.localeCompare(b.ForecastPeriod)
  );
  return sorted[Math.min(monthIndex, sorted.length - 1)];
};

// Mirrors the saved -> old -> model priority the single-record edit screen uses
// (see the ndcValueTypeUsing effect in useUnconstrainedForecastEdit.ts) so the bulk
// edit grid's default AdjForecast/percentadj/stratCode match what a user would see
// opening the same NDC in the single edit screen.
const applyDefaultValuePriority = (
  rows: bulkEditDataInterface[]
): bulkEditDataInterface[] => {
  const result = deepCopy(rows);
  result.forEach(row => {
    row.forecast_data.forEach(month => {
      if (row.saved_values) {
        month.AdjForecast = Number(month.savedAdjValue);
        month.percentadj = Number(month.savedAdjPerc) || 0;
        month.stratCode = month.savedStratCode;
      } else if (row.old_values_present) {
        month.AdjForecast = Number(month.oldAdjForecast);
        month.percentadj = Number(month.oldPercentAdj) || 0;
        month.stratCode = month.oldStratCode;
      }
    });
  });
  return result;
};

export const useBulkEditRoot = () => {
  const { UserStore, UnconstrainedForecastViewStore } = useStore();
  const { requestProgressRefresh } = UnconstrainedForecastViewStore;
  const userName = UserStore?.userMetadata?.userInfo?.name ?? 'User';

  const [editData, setEditData] = useState<bulkEditDataInterface[] | []>([]);
  const [editDataCopy, setEditDataCopy] = useState<bulkEditDataInterface[] | []>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [filterpayload, setFilterpayload] = useState<filterInterface>({
    Ramping: '',
    Concept: '',
    MoleculeAndDosageForm: '',
    StrategyCode: '',
    SellingCategory: '',
    NDC11: '',
    ConceptSort: '',
    SupplierName: '',
    ContractType: '',
  });
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [ndcNotesSet, setNdcNotesSet] = useState<Set<string>>(new Set());
  const [ndcEditedSet, setNdcEditedSet] = useState<Set<string>>(new Set());
  const [ndcError, setNdcError] = useState<Set<string>>(new Set());
  const [ndcSaveLoading, setNdcSaveLoading] = useState<Map<string, boolean>>(new Map());
  const [isSaveAllLoading, setIsSaveloading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [nextPageLoading, setNextPageLoading] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const initialLoadDoneRef = useRef<boolean>(false);
  const isLoadingMoreRef = useRef<boolean>(false);

  // Accordion expansion
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Model/Manual mode per NDC — true = Manual, false = Model
  const [modeState, setModeState] = useState<Map<string, boolean>>(new Map());

  const [singleSaveModalVisibility, setSingleSaveModalVisibility] = useState<boolean>(false);
  const [saveAllModalVisibility, setSaveAllModalVisibility] = useState<boolean>(false);

  // Notes states
  const [isNotesLoading, setIsNotesLoading] = useState<boolean>(false);
  const [notesData, setNotesData] = useState<notesInterface[]>();
  const [isNoteSaving, setIsNoteSaving] = useState<boolean>(false);
  const [_isNoteEditing, setIsNoteEditing] = useState<boolean>(false);
  const [isNoteEdited, setIsNoteEdited] = useState<Map<number, boolean>>(new Map());
  const [isNoteDeleted, setIsNoteDeleted] = useState<Map<number, boolean>>(new Map());
  const [deletingNote, setDeletingNote] = useState<notesInterface | null>(null);
  const [isNoteDeleting, setIsNoteDeleting] = useState<boolean>(false);
  const [isDeleteClicked, setIsDeleteClicked] = useState<boolean>(false);
  const [_latestNote, _setLatestNote] = useState<notesInterface>();
  const [focusedRowForNoteAdd, setFocusedRowForNoteAdd] = useState<number | null>(null);

  // Alert states
  const [showCancelAlert, setShowCancelAlert] = useState<boolean>(false);
  const [showFilterAlert, setShowFilterAlert] = useState<boolean>(false);
  const navigate = useNavigate();

  // ─── Mode (Model / Manual) ────────────────────────────────────────────────

  const initializeModeState = (data: bulkEditDataInterface[], reset = false) => {
    setModeState(prev => {
      const newMap = reset ? new Map<string, boolean>() : new Map(prev);
      data.forEach(row => {
        if (!newMap.has(row.NDC11)) {
          const isManual = row.forecast_data.some(fd => fd.stratCode.toLowerCase() === 'manual');
          newMap.set(row.NDC11, isManual);
        }
      });
      return newMap;
    });
  };

  const getModeState = (rowIndex: number): boolean => {
    if (!editData[rowIndex]) return false;
    return modeState.get(editData[rowIndex].NDC11) ?? false;
  };

  const handleModeToggle = async (rowIndex: number, isManual: boolean) => {
    if (!editData[rowIndex]) return;
    const ndcKey = editData[rowIndex].NDC11;
    const oldMode = modeState.get(ndcKey) ? 'Manual' : 'Model';
    const newMode = isManual ? 'Manual' : 'Model';

    setModeState(prev => {
      const m = new Map(prev);
      m.set(ndcKey, isManual);
      return m;
    });

    if (!isManual) {
      // Model mode always reflects the system baseline forecast for the current
      // planning period, regardless of what value type (saved/old/forecast) was
      // originally shown.
      const tempData = deepCopy(editData);
      tempData[rowIndex].forecast_data.forEach(month => {
        month.AdjForecast = Number(month.Baseline);
        month.percentadj = 0;
      });
      setEditData(tempData);

      const hasChanged = hasUnconstrainedForecastDifferences(
        editDataCopy[rowIndex].forecast_data,
        tempData[rowIndex].forecast_data
      );
      setNdcEditedSet(prev => {
        const next = new Set(prev);
        if (hasChanged) { next.add(ndcKey); } else { next.delete(ndcKey); }
        return next;
      });

      setNdcError(prev => {
        const s = new Set(prev);
        s.delete(ndcKey);
        return s;
      });
    } else if (editData[rowIndex].saved_values || editData[rowIndex].old_values_present) {
      // Manual mode restores whichever prior value the single edit screen would
      // fall back to: saved values take priority over old values.
      const useSaved = editData[rowIndex].saved_values;
      const tempData = deepCopy(editData);
      tempData[rowIndex].forecast_data.forEach(month => {
        month.AdjForecast = Number(useSaved ? month.savedAdjValue : month.oldAdjForecast);
        month.percentadj = Number(useSaved ? month.savedAdjPerc : month.oldPercentAdj) || 0;
      });
      setEditData(tempData);

      const hasChanged = hasUnconstrainedForecastDifferences(
        editDataCopy[rowIndex].forecast_data,
        tempData[rowIndex].forecast_data
      );
      setNdcEditedSet(prev => {
        const next = new Set(prev);
        if (hasChanged) { next.add(ndcKey); } else { next.delete(ndcKey); }
        return next;
      });

      setNdcError(prev => {
        const s = new Set(prev);
        s.delete(ndcKey);
        return s;
      });
    }

    // Auto-save note recording the mode change
    const today = new Date().toISOString().split('T')[0];
    const note = `Status changed from ${oldMode} to ${newMode} by ${userName} on ${today}`;
    try {
      await unconstrainedForecastService.saveNDC11Note({
        ndc11: ndcKey,
        note,
        planning_type: 'unconstrained',
      });
      setNdcNotesSet(prev => {
        const s = new Set(prev);
        s.add(ndcKey);
        return s;
      });
    } catch (_err) {
      console.log('Error saving auto-note for mode toggle');
    }
  };

  // ─── Accordion ───────────────────────────────────────────────────────────

  const toggleRowExpansion = (rowIndex: number) => {
    setExpandedRows(prev => {
      const s = new Set(prev);
      if (s.has(rowIndex)) { s.delete(rowIndex); } else { s.add(rowIndex); }
      return s;
    });
  };

  const isRowExpanded = (rowIndex: number): boolean => expandedRows.has(rowIndex);

  // Returns previous planning period value for the expanded row
  const getBaselineValue = (
    rowIndex: number,
    forecastPeriod: string,
    monthIndex?: number
  ): string => {
    const row = editData[rowIndex];
    if (!row || !row.old_values_present) return '-';
    const month = findForecastEntryByPeriodOrIndex(row.forecast_data, forecastPeriod, monthIndex);
    return month ? String(month.oldAdjForecast) : '-';
  };

  // ─── Status ──────────────────────────────────────────────────────────────

  const getRowStatus = (rowIndex: number): 'Model' | 'Manual' =>
    getModeState(rowIndex) ? 'Manual' : 'Model';

  // Mirrors initializeModeState logic on raw data so the StrategyCode filter
  // can be enforced client-side, guaranteeing the Status column always matches
  // the selected filter. stratCode here is already normalized by
  // applyDefaultValuePriority to whichever source (savedStratCode / oldStratCode
  // / raw model stratCode) is actually in effect - each of those is now a
  // straight passthrough of the DB's StratCode column, so this agrees with the
  // backend's own pre-filter in unconstrained_bulk_edit_helpter_services.py.
  const computeRowStatusFromData = (row: bulkEditDataInterface): 'model' | 'manual' =>
    row.forecast_data.some(fd => fd.stratCode.toLowerCase() === 'manual') ? 'manual' : 'model';

  const applyStrategyCodeFilter = (
    rows: bulkEditDataInterface[],
    strategyCode: string
  ): bulkEditDataInterface[] => {
    if (!strategyCode) return rows;
    return rows.filter(row => computeRowStatusFromData(row) === strategyCode.toLowerCase());
  };

  // ─── Forecast value helpers ───────────────────────────────────────────────

  const getForecastValue = (ndc: string, period: string, monthIndex?: number): string => {
    const row = editData.find(item => item.NDC11 === ndc);
    if (!row) return '';
    const month = findForecastEntryByPeriodOrIndex(row.forecast_data, period, monthIndex);
    if (!month) return '';
    const n = Number(month.AdjForecast);
    return !isNaN(n) ? n.toLocaleString('en-US') : String(month.AdjForecast);
  };

  const isNumeric = (value: string) => /^[0-9]{1,9}$/.test(value);

  // Mirrors the single-edit screen's rule (resolveStratCodeForSave in
  // useUnconstrainedForecastEdit.ts): a row is Manual whenever any month's
  // adjustment no longer numerically equals its Baseline, Model when every
  // month matches. Recomputed here - not left stale until the switch is
  // explicitly touched - so typing a value that diverges from Baseline is
  // never silently saved as "Model" with a mismatched value.
  const recomputeModeStateFromValues = (ndcKey: string, forecastData: { AdjForecast: number; Baseline: number }[]) => {
    const allMatchBaseline = forecastData.every(fd => Number(fd.AdjForecast) === Number(fd.Baseline));
    setModeState(prev => {
      const m = new Map(prev);
      m.set(ndcKey, !allMatchBaseline);
      return m;
    });
  };

  const setForecastValue = (
    idx: number,
    period: string,
    adjustment: string,
    monthIndex?: number
  ) => {
    adjustment = adjustment.replace(/,/g, '').replace(/^0+/, '') || '0';
    if (!isNumeric(adjustment)) return;

    setFocusedRowForNoteAdd(idx);
    const tempData = deepCopy(editData);
    const month = findForecastEntryByPeriodOrIndex(tempData[idx].forecast_data, period, monthIndex);
    if (month) month.AdjForecast = Number(adjustment);
    setEditData(tempData);

    const hasChanged = hasUnconstrainedForecastDifferences(
      editDataCopy[idx].forecast_data,
      tempData[idx].forecast_data
    );
    setNdcEditedSet(prev => {
      const next = new Set(prev);
      if (hasChanged) { next.add(tempData[idx].NDC11); } else { next.delete(tempData[idx].NDC11); }
      return next;
    });

    recomputeModeStateFromValues(tempData[idx].NDC11, tempData[idx].forecast_data);
  };

  function hasUnconstrainedForecastDifferences(
    a: { ForecastPeriod: string; AdjForecast: number }[],
    b: { ForecastPeriod: string; AdjForecast: number }[]
  ): boolean {
    if (a.length !== b.length) return true;
    for (let i = 0; i < a.length; i++) {
      if (a[i].AdjForecast != b[i].AdjForecast) return true;
    }
    return false;
  }

  const isRowEdited = (idx: number): boolean => {
    if (!editData[idx] || !editDataCopy[idx]) return false;
    return hasUnconstrainedForecastDifferences(
      editDataCopy[idx].forecast_data,
      editData[idx].forecast_data
    );
  };

  const isRowModeChanged = (idx: number): boolean => {
    if (!editData[idx] || !editDataCopy[idx]) return false;
    const copyRow = editDataCopy[idx];
    const originalIsManual = copyRow.forecast_data.some(
      fd => (fd.stratCode as string).toLowerCase() === 'manual'
    );
    const currentIsManual = modeState.get(editData[idx].NDC11) ?? false;
    return originalIsManual !== currentIsManual;
  };

  // Save button enabled when forecast values changed OR the mode (Model/Manual) changed
  const isBtnEnabled = (idx: number): boolean => isRowEdited(idx) || isRowModeChanged(idx);

  const isSalveAllBtnEnabled = () => {
    for (let i = 0; i < editData.length; i++) {
      if (isBtnEnabled(i)) return true;
    }
    return false;
  };

  // ─── Copy-forward ────────────────────────────────────────────────────────

  const handleCopy = (idx: number, month: string) => {
    const tempData = deepCopy(editData);
    const sourceMonth = editData[idx].forecast_data.find(fd => fd.ForecastPeriod === month);
    if (!sourceMonth) return;
    const sourceValue = sourceMonth.AdjForecast;
    const forecastDate = new Date(month);
    for (const item of tempData[idx].forecast_data) {
      if (new Date(item.ForecastPeriod) > forecastDate) {
        item.AdjForecast = Number(sourceValue) || 0;
      }
    }
    const hasChanged = hasUnconstrainedForecastDifferences(
      editDataCopy[idx].forecast_data,
      tempData[idx].forecast_data
    );
    const tempSet = ndcEditedSet;
    if (hasChanged) { tempSet.add(editData[idx].NDC11); } else { tempSet.delete(editData[idx].NDC11); }
    setNdcEditedSet(tempSet);
    setEditData(tempData);

    recomputeModeStateFromValues(tempData[idx].NDC11, tempData[idx].forecast_data);
  };

  // ─── Concept sorting ─────────────────────────────────────────────────────

  const handleConceptSort = (sortType: 'asc' | 'desc' | '') => {
    setEditData([]);
    setEditDataCopy([]);
    setNdcSaveLoading(new Map());
    setNdcEditedSet(new Set());
    setNdcNotesSet(new Set());
    setExpandedRows(new Set());
    setNdcError(new Set());
    setModeState(new Map());

    const newFilterData = { ...filterpayload, ConceptSort: sortType };
    setFilterpayload(newFilterData);
    setHasMore(true);
    setPageNumber(1);
    fetchDataWithSort(newFilterData);
  };

  // ─── Save ────────────────────────────────────────────────────────────────

  const getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };

  const getValidForecastPeriods = (): Set<string> => {
    const periods = new Set<string>();
    const dt = new Date();
    const startMonth = dt.getMonth();
    const startYear = dt.getFullYear();
    for (let i = 0; i < 15; i++) {
      const month = (startMonth + i + 1) % 12;
      const year = startYear + Math.floor((startMonth + i + 1) / 12);
      periods.add(`${year}-${month >= 9 ? month + 1 : `0${month + 1}`}-01`);
    }
    return periods;
  };

  const handleSingleSave = async (idx: number) => {
    const ndcData = editData[idx];
    const ndcKey = ndcData.NDC11;
    const isManual = modeState.get(ndcKey) ?? false;

    setNdcError(prev => {
      const s = new Set(prev);
      s.delete(ndcKey);
      return s;
    });

    const planning_period = getCurrentDate();
    const validPeriods = getValidForecastPeriods();
    const payload = ndcData.forecast_data
      .filter(item => validPeriods.has(item.ForecastPeriod))
      .map(item => ({
        NDC11: ndcData.NDC11,
        PlanningPeriod: planning_period,
        ForecastPeriod: item.ForecastPeriod,
        StratCode: isManual ? 'Manual' : 'Model',
        WholesalerDemandForecast: item.is_deramped ? 0 : Math.round(Number(item.Baseline)),
        Adjustment: item.is_deramped ? 0 : Math.round(Number(item.AdjForecast) - Number(item.Baseline)),
        UnconstrainedForecast: item.is_deramped ? 0 : Math.round(Number(item.AdjForecast)),
        Notes: '',
        SellingCategory: ndcData.SellingCategory,
      }));

    try {
      setNdcSaveLoading(prev => {
        const m = new Map(prev);
        m.set(ndcKey, true);
        return m;
      });
      const data = await unconstrainedForecastService.saveUnconstrainedForecast(payload);
      if (data) {
        setFocusedRowForNoteAdd(null);
        toast.success('Unconstrained forecast data is saved successfully.');
        markTheRowsSaved([idx]);
        setSingleSaveModalVisibility(prev => !prev);
        setNdcEditedSet(prev => {
          const s = new Set(prev);
          s.delete(ndcKey);
          return s;
        });
      }
    } catch (_error) {
      toast.error('Something went wrong while saving Unconstrained forecast');
    } finally {
      setNdcSaveLoading(prev => {
        const m = new Map(prev);
        m.set(ndcKey, false);
        return m;
      });
      requestProgressRefresh();
    }
  };

  const markTheRowsSaved = (rowIndexes: number[]) => {
    try {
      const tempEditData = deepCopy(editData);
      const tempEditDataCopy = deepCopy(editDataCopy);
      rowIndexes.forEach(index => {
        tempEditData[index].saved_values = true;
        tempEditDataCopy[index].saved_values = true;
        const currentStratCode = (modeState.get(tempEditData[index].NDC11) ?? false) ? 'Manual' : 'Model';
        tempEditData[index].forecast_data.forEach((element, i) => {
          const adjValue = Number(element.AdjForecast);
          element.savedAdjValue = adjValue;
          element.stratCode = currentStratCode;
          // Sync the copy so isRowEdited returns false and wasOriginallyManual check is accurate
          tempEditDataCopy[index].forecast_data[i].AdjForecast = adjValue;
          tempEditDataCopy[index].forecast_data[i].savedAdjValue = adjValue;
          tempEditDataCopy[index].forecast_data[i].stratCode = currentStratCode;
        });
      });
      setEditData(tempEditData);
      setEditDataCopy(tempEditDataCopy);
    } catch (_error) {
      console.log('Error while marking the rows saved', _error);
    }
  };

  const handleSaveAllEdited = async () => {
    setNdcError(new Set());
    const rowsToSave: number[] = [];
    for (let i = 0; i < editData.length; i++) {
      if (isBtnEnabled(i)) rowsToSave.push(i);
    }
    if (rowsToSave.length === 0) return;

    const planning_period = getCurrentDate();
    const validPeriods = getValidForecastPeriods();
    const payload: {
      NDC11: string;
      PlanningPeriod: string;
      ForecastPeriod: string;
      StratCode: string;
      WholesalerDemandForecast: number;
      Adjustment: number;
      UnconstrainedForecast: number;
      Notes: string;
      SellingCategory: string;
    }[] = [];

    for (const rowIndex of rowsToSave) {
      const data = editData[rowIndex];
      const isManual = modeState.get(data.NDC11) ?? false;
      data.forecast_data
        .filter(ele => validPeriods.has(ele.ForecastPeriod))
        .forEach(ele => {
          payload.push({
            NDC11: data.NDC11,
            PlanningPeriod: planning_period,
            ForecastPeriod: ele.ForecastPeriod,
            StratCode: isManual ? 'Manual' : 'Model',
            WholesalerDemandForecast: ele.is_deramped ? 0 : Math.round(Number(ele.Baseline)),
            Adjustment: ele.is_deramped ? 0 : Math.round(Number(ele.AdjForecast) - Number(ele.Baseline)),
            UnconstrainedForecast: ele.is_deramped ? 0 : Math.round(Number(ele.AdjForecast)),
            Notes: '',
            SellingCategory: data.SellingCategory,
          });
        });
    }

    setIsSaveloading(true);
    try {
      if (payload.length > 0) {
        const data = await unconstrainedForecastService.saveUnconstrainedForecast(payload);
        if (data) {
          toast.success(
            'Successfully saved Unconstrained forecast data for all the highlighted rows.'
          );
          markTheRowsSaved(rowsToSave);
          setSaveAllModalVisibility(prev => !prev);
          setNdcNotesSet(new Set());
        }
      }
    } catch (_error) {
      toast.error('Something went wrong while saving the unconstrained forecast.');
    } finally {
      setIsSaveloading(false);
      setNdcEditedSet(new Set());
      setNdcNotesSet(new Set());
      requestProgressRefresh();
    }
  };

  // ─── Notes ────────────────────────────────────────────────────────────────

  const getNotesData = async (ndc: string) => {
    try {
      setIsNotesLoading(true);
      const data = await unconstrainedForecastService.getNDC11Note(ndc, 'unconstrained');
      if (data) setNotesData(data);
    } catch (_error) {
      console.log('Error while getting notes data');
    } finally {
      setIsNotesLoading(false);
    }
  };

  const handleAddNote = async (ndc: string, value: string) => {
    let data: notesInterface[] = [];
    try {
      setIsNoteSaving(true);
      if (value?.trim() === '') {
        toast.warning('Cannot save an empty note!!');
      } else {
        const payload = { ndc11: ndc, note: value, planning_type: 'unconstrained' };
        data = await unconstrainedForecastService.saveNDC11Note(payload);
        setNdcNotesSet(prev => {
          const s = new Set(prev);
          s.add(ndc);
          return s;
        });
        setNdcError(prev => {
          const s = new Set(prev);
          s.delete(ndc);
          return s;
        });
      }
      return true;
    } catch (_error) {
      toast.error('Something went wrong!!');
    } finally {
      setNotesData(data);
      setIsNoteSaving(false);
    }
    return false;
  };

  const handleEditNote = async (id: number, note: string) => {
    try {
      setIsNoteEditing(true);
      const data = await unconstrainedForecastService.updateNote(id, note);
      if (data.status && notesData) {
        notesData.map(item => {
          if (item.ID === id) {
            item.CreatedDate = convertIsoToDateString(data.lastUpdatedDate) || '';
          }
        });
        toast.success('Note Updated successfully.');
      }
    } catch (_error) {
      toast.error('Something went wrong.');
    } finally {
      setIsNoteEditing(false);
    }
  };

  const handleDeleteNote = async (id: number) => {
    if (focusedRowForNoteAdd === null) return;
    try {
      setIsNoteDeleting(true);
      const data = await unconstrainedForecastService.deleteNote(id);
      if (data) {
        toast.success('Note deleted successfully.');
        if (notesData) {
          setNotesData([...notesData.filter(note => note.ID !== id)]);
        }
      }
    } catch (_error) {
      toast.error('Something went wrong.');
    } finally {
      setIsNoteDeleting(false);
      setIsDeleteClicked(false);
    }
  };

  const isNotesBtnEnabled = (idx: number) => {
    if (idx < 0 || idx >= editData.length || !editData[idx]) return false;
    const ndcKey = editData[idx].NDC11;
    return ndcEditedSet.has(ndcKey);
  };

  // ─── Data fetching ────────────────────────────────────────────────────────

  const fetchDataWithSort = async (filterDataWithSort: filterInterface) => {
    try {
      setIsDataLoading(true);
      setInitialLoading(true);
      const data = await unconstrainedForecastService.getFilteredData({
        page_no: 1,
        filters: filterDataWithSort,
      });
      setPageNumber(2);
      if (!data || data.length === 0) {
        setHasMore(false);
        return;
      }
      const filteredData = applyStrategyCodeFilter(
        applyDefaultValuePriority(data),
        filterDataWithSort.StrategyCode
      );
      if (filteredData.length === 0) {
        setHasMore(false);
        return;
      }
      setEditData(filteredData);
      setEditDataCopy(deepCopy(filteredData));
      initializeModeState(filteredData, true);
    } catch (_error) {
      console.log('Error while getting the data');
    } finally {
      setIsDataLoading(false);
      setInitialLoading(false);
      initialLoadDoneRef.current = true;
    }
  };

  const getNextPageData = async () => {
    try {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const data = await unconstrainedForecastService.getFilteredData(
        { page_no: pageNumber, filters: filterpayload },
        abortController.signal
      );

      if (!data || data.length === 0) {
        setHasMore(false);
        return;
      }
      // Apply the same client-side StrategyCode filter used in fetchDataWithSort so
      // pagination results are always consistent with what the Status column displays.
      const filteredNewData = applyStrategyCodeFilter(
        applyDefaultValuePriority(data),
        filterpayload.StrategyCode
      );
      if (filteredNewData.length === 0 && data.length > 0) {
        setHasMore(false);
        return;
      }
      const newEditData = [...editData, ...filteredNewData];
      const newEditDataCopy = [...editDataCopy, ...deepCopy(filteredNewData)];
      setEditData(newEditData);
      setEditDataCopy(newEditDataCopy);
      setPageNumber(prev => prev + 1);
      initializeModeState(filteredNewData);
    } catch (error: any) {
      const isCancelled =
        error.name === 'AbortError' ||
        error.name === 'CanceledError' ||
        error.code === 'ERR_CANCELED';
      if (!isCancelled) {
        toast.error('Something went wrong while fetching the data');
      }
    } finally {
      isLoadingMoreRef.current = false;
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        setNextPageLoading(false);
        setInitialLoading(false);
      }
    }
  };

  const loadMore = () => {
    if (isLoadingMoreRef.current || !hasMore || !initialLoadDoneRef.current) return;
    isLoadingMoreRef.current = true;
    setNextPageLoading(true);
    getNextPageData();
  };

  const isRowsEdited = () => {
    for (let i = 0; i < editData.length; i++) {
      if (
        hasUnconstrainedForecastDifferences(
          editDataCopy[i].forecast_data,
          editData[i].forecast_data
        )
      )
        return true;
    }
    return false;
  };

  const handleApplyFilter = (newFilterData: filterInterface) => {
    // Abort any in-flight pagination to prevent stale unfiltered data overwriting filtered results
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    isLoadingMoreRef.current = false;

    setEditData([]);
    setEditDataCopy([]);
    setPageNumber(1);
    setIsDataLoading(false);
    setNdcNotesSet(new Set());
    setNdcError(new Set());
    setNdcSaveLoading(new Map());
    setIsSaveloading(false);
    setIsNotesLoading(false);
    setNotesData([]);
    setIsNoteSaving(false);
    setIsNoteEdited(new Map());
    setIsNoteDeleted(new Map());
    setHasMore(true);
    setNdcEditedSet(new Set());
    setNextPageLoading(false);
    setShowCancelAlert(false);
    setShowFilterAlert(false);
    setSingleSaveModalVisibility(false);
    setSaveAllModalVisibility(false);
    setIsDeleteClicked(false);
    setDeletingNote(null);
    setInitialLoading(true);
    setExpandedRows(new Set());
    setModeState(new Map());
    setFilterpayload(newFilterData);
    fetchDataWithSort(newFilterData);
  };

  const handleCancelButtonClick = () => {
    if (isRowsEdited()) {
      setShowCancelAlert(true);
    } else {
      navigate(-1);
    }
  };

  const getInitialData = async () => {
    try {
      setIsDataLoading(true);
      setInitialLoading(true);
      const data = await unconstrainedForecastService.getFilteredData({
        page_no: 1,
        filters: filterpayload,
      });
      setPageNumber(2);
      if (!data || data.length === 0) {
        setHasMore(false);
        return;
      }
      const normalizedData = applyDefaultValuePriority(data);
      setEditData(normalizedData);
      setEditDataCopy(deepCopy(normalizedData));
      initializeModeState(normalizedData, true);
    } catch (_error) {
      console.log('Error while getting the initial data');
    } finally {
      setIsDataLoading(false);
      setInitialLoading(false);
      initialLoadDoneRef.current = true;
    }
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    editData,
    setEditData,
    setEditDataCopy,
    pageNumber,
    setPageNumber,
    filterpayload,
    setFilterpayload,
    isDataLoading,
    setIsDataLoading,
    getForecastValue,
    setForecastValue,
    isRowEdited,
    isBtnEnabled,
    isNotesBtnEnabled,
    isSalveAllBtnEnabled,
    ndcNotesSet,
    setNdcNotesSet,
    ndcError,
    setNdcError,
    handleSingleSave,
    handleSaveAllEdited,
    ndcSaveLoading,
    isSaveAllLoading,
    isNotesLoading,
    notesData,
    setNotesData,
    isNoteSaving,
    getNotesData,
    handleAddNote,
    hasMore,
    nextPageLoading,
    loadMore,
    showCancelAlert,
    setShowCancelAlert,
    showFilterAlert,
    setShowFilterAlert,
    isRowsEdited,
    singleSaveModalVisibility,
    setSingleSaveModalVisibility,
    saveAllModalVisibility,
    setSaveAllModalVisibility,
    isNoteEdited,
    setIsNoteEdited,
    isNoteDeleted,
    setIsNoteDeleted,
    handleCopy,
    handleEditNote,
    handleDeleteNote,
    deletingNote,
    isNoteDeleting,
    isDeleteClicked,
    setIsDeleteClicked,
    setDeletingNote,
    initialLoading,
    handleApplyFilter,
    handleCancelButtonClick,
    expandedRows,
    toggleRowExpansion,
    isRowExpanded,
    getBaselineValue,
    getModeState,
    handleModeToggle,
    getRowStatus,
    handleConceptSort,
    markTheRowsSaved,
  };
};
